import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import './FoodTracker.css';

const toISODate = (d) => d.toISOString().slice(0, 10);

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function fmtNum(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function opLabel(op) {
  if (!op) return "";
  if (op === "lt") return "<";
  if (op === "gt") return ">";
  if (op === "eq") return "=";
  return String(op);
}

function metricProgressPct(rule, actual) {
  if (!rule) return null;

  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  const target = Number(rule.value ?? rule.target ?? rule.threshold);
  if (!Number.isFinite(target) || target <= 0) return null;

  const a = actual == null ? 0 : Number(actual);
  if (!Number.isFinite(a)) return null;

  const MIN_SLIVER = 4;
  const normalized = clamp((a / target) * 100, 0, 100);

  if (op === "gt") {
    return normalized === 0 ? MIN_SLIVER : Math.max(MIN_SLIVER, normalized);
  }

  if (op === "lt") {
    const base = normalized === 0 ? MIN_SLIVER : Math.max(MIN_SLIVER, normalized);
    return a > target ? 100 : base;
  }

  if (op === "eq") {
    return a === target ? 100 : MIN_SLIVER;
  }

  return null;
}

function metricProgressText(rule, actual) {
  if (!rule) return "";
  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  const target = Number(rule.value ?? rule.target ?? rule.threshold);
  const a = actual == null ? null : Number(actual);

  if (!Number.isFinite(target)) return "";

  if (op === "lt") {
    if (!Number.isFinite(a)) return `Stay under ${fmtNum(target)}`;
    const remaining = target - a;
    return remaining >= 0
      ? `${fmtNum(remaining)} below limit`
      : `${fmtNum(Math.abs(remaining))} over limit`;
  }

  if (op === "gt") {
    if (!Number.isFinite(a)) return `Reach ${fmtNum(target)}`;
    const remaining = target - a;
    return remaining <= 0
      ? `${fmtNum(Math.abs(remaining))} above goal`
      : `${fmtNum(remaining)} to goal`;
  }

  if (op === "eq") {
    if (!Number.isFinite(a)) return `Target: ${fmtNum(target)}`;
    return a === target ? "On target" : `Target: ${fmtNum(target)}`;
  }

  return "";
}

function getMetricRingVisual(rule, actual) {
  const pct = metricProgressPct(rule, actual);
  if (pct == null) return null;

  const op = String(rule?.operator ?? rule?.op ?? "").toLowerCase();
  const target = Number(rule?.value ?? rule?.target ?? rule?.threshold);
  const a = actual == null ? 0 : Number(actual);
  const ratio = Number.isFinite(target) && target > 0 && Number.isFinite(a) ? clamp(a / target, 0, 1) : 0;

  let color = "#f59e0b";
  if (op === "gt") {
    color = a >= target ? "#22c55e" : ratio >= 0.5 ? "#f59e0b" : "#ef4444";
  } else if (op === "lt") {
    color = a > target ? "#ef4444" : ratio >= 0.5 ? "#f59e0b" : "#22c55e";
  } else if (op === "eq") {
    color = a === target ? "#22c55e" : "#f59e0b";
  }

  return { pct, color };
}

function isNutritionHabit(h) {
  if (!h) return false;
  const metricPath = [
    h?.metric_rule?.domain,
    h?.metric_rule?.field,
    h?.metric_key,
    h?.domain,
    h?.field,
    h?.category,
    h?.type,
    h?.name,
    h?.title,
    h?.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const nutritionHints = [
    "food", "nutrition", "meal", "protein", "carb", "fat", "calorie", "water",
    "macro", "hydration", "app.protein_g", "app.carbs_g", "app.fats_g", "app.calories_consumed", "app.water_oz"
  ];

  return nutritionHints.some((x) => metricPath.includes(x));
}

function MetricHabitRing({ rule, actual, label }) {
  const visual = getMetricRingVisual(rule, actual);
  const size = 106;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = visual?.pct ?? 0;
  const dash = circumference * (pct / 100);
  const target = Number(rule?.value ?? rule?.target ?? rule?.threshold);

  return (
    <div className="tracker-habit-ring" aria-label={label || "metric habit progress"}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="tracker-habit-ring-svg">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={visual?.color || "#f59e0b"}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="tracker-habit-ring-center">
        <div className="tracker-habit-ring-value">{actual == null ? "—" : fmtNum(actual)}</div>
        <div className="tracker-habit-ring-target">{opLabel(rule?.operator || rule?.op)} {fmtNum(target)}</div>
      </div>
    </div>
  );
}


function useIsMobileFood(breakpoint = 809) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [breakpoint]);

  return isMobile;
}

function FoodMobileCarousel({ children, className = "", slideClassName = "" }) {
  const isMobile = useIsMobileFood();
  const trackRef = useRef(null);
  const items = React.Children.toArray(children).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);

  const getSlides = useCallback(() => {
    const el = trackRef.current;
    if (!el) return [];
    return Array.from(el.children);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    const el = trackRef.current;
    if (!el) return;

    if (!isMobile) {
      el.scrollLeft = 0;
      return;
    }

    const slides = getSlides();
    if (!slides.length) {
      el.scrollLeft = 0;
      return;
    }

    const first = slides[0];
    const left = first.offsetLeft - (el.clientWidth - first.offsetWidth) / 2;
    el.scrollLeft = Math.max(0, left);
  }, [items.length, isMobile, getSlides]);

  const syncIndex = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const slides = getSlides();
    if (!slides.length) return;

    const viewportCenter = el.scrollLeft + el.clientWidth / 2;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    setActiveIndex(clamp(bestIndex, 0, Math.max(items.length - 1, 0)));
  }, [items.length, getSlides]);

  const scrollToIndex = useCallback(
    (index) => {
      const el = trackRef.current;
      if (!el) return;

      const slides = getSlides();
      if (!slides.length) return;

      const next = clamp(index, 0, Math.max(items.length - 1, 0));
      const target = slides[next];
      if (!target) return;

      const left = target.offsetLeft - (el.clientWidth - target.offsetWidth) / 2;
      el.scrollTo({ left, behavior: "smooth" });
      setActiveIndex(next);
    },
    [getSlides, items.length]
  );

  if (!isMobile) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div>
    <div className={`food-mobile-carousel-shell ${className}`.trim()}>
      <div
        ref={trackRef}
        className="food-mobile-carousel-track"
        onScroll={syncIndex}
      >
        {items.map((child, index) => (
          <div
            key={index}
            className={`food-mobile-carousel-slide ${slideClassName}`.trim()}
            onClick={() => {
              if (items.length <= 1) return;
              scrollToIndex(activeIndex >= items.length - 1 ? 0 : activeIndex + 1);
            }}
          >
            {child}
          </div>
        ))}
      </div>

      
    </div>
    {items.length > 1 ? (
        <div className="food-mobile-carousel-dots" role="tablist" aria-label="Carousel pages">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`food-mobile-carousel-dot ${index === activeIndex ? "is-active" : ""}`}
              aria-label={`Go to slide ${index + 1}`}
              aria-selected={index === activeIndex}
              onClick={(e) => {
                e.stopPropagation();
                scrollToIndex(index);
              }}
            />
          ))}
        </div>
      ) : null}
      </div>
  );
}

function FoodTracker({
  onNavigate,
  token,
  foodLogs,
  mealFeedback,
  onSaveMealFeedback,
  onStartCamera,
  onFileUpload,
  onDeleteLog,
  onAddToFavorites,
  onManualLog,
  onEditLog,
  openManualTrigger,
  openQuickAddTrigger,
  fileInputRef,
  onOpenLogChoice,
  waterLogs = [],
  onAddWater,
  onDeleteWaterLog,
  showToast,
  foodFavorites = [],
  onToggleFoodFavorite,
  onRefreshFood,
}) {
  const localFileInputRef = useRef(null);
  const fileInputRefToUse = fileInputRef || localFileInputRef;
  const toast = (message, type = "info") => showToast?.(message, type);

  const [showManualForm, setShowManualForm] = useState(false);
  const [showQuickAddForm, setShowQuickAddForm] = useState(false);
  const [quickFoodKey, setQuickFoodKey] = useState("");
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [editingLog, setEditingLog] = useState(null);

  const [selectedDate, setSelectedDate] = useState(() => toISODate(new Date()));
  const today = useMemo(() => toISODate(new Date()), []);

  const [details, setDetails] = useState("");
  const [showUploadDetails, setShowUploadDetails] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);

  const [formData, setFormData] = useState({
    food_name: '',
    quantity: '1 serving',
    meal_type: 'lunch',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    fiber: ''
  });

  const [waterOzCustom, setWaterOzCustom] = useState('');
  const [showAddWaterModal, setShowAddWaterModal] = useState(false);
  const [waterBusy, setWaterBusy] = useState(false);
  const [programMeals, setProgramMeals] = useState([]);
  const [programMealsLoading, setProgramMealsLoading] = useState(false);
  const [programMealBusyKey, setProgramMealBusyKey] = useState('');
  const [recipeMeal, setRecipeMeal] = useState(null);

  const [nutritionHabits, setNutritionHabits] = useState([]);
  const [nutritionHabitsLoading, setNutritionHabitsLoading] = useState(false);
  const [nutritionHabitBusyId, setNutritionHabitBusyId] = useState('');
  const [showCreateHabitModal, setShowCreateHabitModal] = useState(false);
  const [newHabitForm, setNewHabitForm] = useState({
    name: '',
    description: '',
    habit_type: 'metric',
    time_of_day: '12:00',
    metric_domain: 'app',
    metric_field: 'protein_g',
    metric_operator: 'gt',
    metric_value: '',
  });

  const [mealRatings, setMealRatings] = useState({});
  const [mealRatingBusy, setMealRatingBusy] = useState("");
  const safeMealFeedback = useMemo(
  () => (Array.isArray(mealFeedback) ? mealFeedback : []),
  [mealFeedback]
);


  const favoriteFoodMap = useMemo(() => {
    const map = new Map();
    (foodFavorites || []).forEach((fav) => {
      const key = String(fav?.food_name || '').trim().toLowerCase();
      if (key) map.set(key, fav);
    });
    return map;
  }, [foodFavorites]);

  const getFavoriteForFood = useCallback((foodName) => {
    const key = String(foodName || '').trim().toLowerCase();
    return key ? favoriteFoodMap.get(key) || null : null;
  }, [favoriteFoodMap]);

  const isTodaySelected = selectedDate === today;

  const lastManualTriggerRef = useRef(openManualTrigger);
  const lastQuickAddTriggerRef = useRef(openQuickAddTrigger);

  const closeAllFoodModals = useCallback(() => {
    setShowManualForm(false);
    setShowQuickAddForm(false);
    setShowUploadDetails(false);
    setPendingUploadFile(null);
    setRecipeMeal(null);
    setEditingLog(null);
    setQuickFoodKey("");
    setShowCreateHabitModal(false);
    setShowAddWaterModal(false);
  }, []);

  useEffect(() => {
    if (
      openManualTrigger != null &&
      openManualTrigger !== lastManualTriggerRef.current
    ) {
      setShowQuickAddForm(false);
      setShowManualForm(true);
      lastManualTriggerRef.current = openManualTrigger;
    }
  }, [openManualTrigger]);

  useEffect(() => {
    if (
      openQuickAddTrigger != null &&
      openQuickAddTrigger !== lastQuickAddTriggerRef.current
    ) {
      setShowManualForm(false);
      setShowQuickAddForm(true);
      lastQuickAddTriggerRef.current = openQuickAddTrigger;
    }
  }, [openQuickAddTrigger]);

  const logsForSelectedDate = useMemo(() => {
    return (foodLogs || []).filter((log) => {
      if (log?.date) return String(log.date).slice(0, 10) === selectedDate;
      return selectedDate === today;
    });
  }, [foodLogs, selectedDate, today]);

  const waterForSelectedDate = useMemo(() => {
    return (waterLogs || []).filter((w) => {
      if (w?.date) return String(w.date).slice(0, 10) === selectedDate;
      return selectedDate === today;
    });
  }, [waterLogs, selectedDate, today]);

  const totalWaterOz = useMemo(() => {
    return waterForSelectedDate.reduce((sum, w) => sum + (Number(w.ounces) || 0), 0);
  }, [waterForSelectedDate]);


  const beverageWaterEntries = useMemo(() => {
    return waterForSelectedDate
      .slice()
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
      .map((w) => ({
        ...w,
        log_id: `water:${w.water_log_id || `${w.date}-${w.ounces}-${w.created_at}`}`,
        food_name: 'Water',
        quantity: `${Number(w.ounces) || 0} oz`,
        calories: 0,
        protein: 0,
        carbs: 0,
        fats: 0,
        fiber: 0,
        meal_type: 'beverage',
        isWater: true,
      }));
  }, [waterForSelectedDate]);

  const dailyNutritionTotals = useMemo(() => {
    return (logsForSelectedDate || []).reduce(
      (sum, log) => ({
        calories: sum.calories + (Number(log?.calories) || 0),
        protein: sum.protein + (Number(log?.protein) || 0),
        carbs: sum.carbs + (Number(log?.carbs) || 0),
        fats: sum.fats + (Number(log?.fats) || 0),
        fiber: sum.fiber + (Number(log?.fiber) || Number(log?.fiber_g) || 0),
      }),
      { calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0 }
    );
  }, [logsForSelectedDate]);

  const visibleProgramMeals = useMemo(() => {
    const normalizedLogs = (logsForSelectedDate || []).map((log) => ({
      meal_type: String(log?.meal_type || '').toLowerCase(),
      food_name: String(log?.food_name || '').trim().toLowerCase(),
      calories: Number(log?.calories) || 0,
      protein: Number(log?.protein) || 0,
      carbs: Number(log?.carbs) || 0,
      fats: Number(log?.fats) || 0,
    }));

    return (programMeals || []).filter((meal) => {
      const mealType = String(meal?.meal_type || '').toLowerCase();
      const title = String(meal?.title || meal?.meal_name || meal?.name || '').trim().toLowerCase();
      const cal = Number(meal?.calories) || 0;
      const protein = Number(meal?.protein) || 0;
      const carbs = Number(meal?.carbs) || 0;
      const fats = Number(meal?.fats) || 0;

      return !normalizedLogs.some((log) => {
        if (log.meal_type !== mealType) return false;
        if (log.food_name === title) return true;

        const sameMacros =
          Math.abs(log.calories - cal) < 0.01 &&
          Math.abs(log.protein - protein) < 0.01 &&
          Math.abs(log.carbs - carbs) < 0.01 &&
          Math.abs(log.fats - fats) < 0.01;

        return sameMacros;
      });
    });
  }, [programMeals, logsForSelectedDate]);

  const quickAddFoodOptions = useMemo(() => {
    const previousLogged = (logsForSelectedDate || []).map((log) => ({
      key: `log:${log.log_id}`,
      label: log.food_name || "Food",
      food_name: log.food_name || "",
      quantity: "1 serving",
      meal_type: log.meal_type || "lunch",
      calories: Number(log.calories) || 0,
      protein: Number(log.protein) || 0,
      carbs: Number(log.carbs) || 0,
      fats: Number(log.fats) || 0,
      recipe_data: Array.isArray(log.recipe_data) ? log.recipe_data : [],
      source: "history",
    }));

    const uniqueMap = new Map();
    previousLogged.forEach((item) => {
      const key = String(item.food_name || "").trim().toLowerCase();
      if (!key || uniqueMap.has(key)) return;
      uniqueMap.set(key, item);
    });

    const dedupedPrevious = Array.from(uniqueMap.values());

    if (dedupedPrevious.length > 0) {
      return dedupedPrevious;
    }

    return (programMeals || []).map((meal, idx) => ({
      key: `meal:${meal.id || idx}`,
      label: meal.meal_name || meal.name || meal.title || "Planned meal",
      food_name: meal.meal_name || meal.name || meal.title || "Planned meal",
      quantity: "1 serving",
      meal_type: meal.meal_type || "lunch",
      calories: Number(meal.calories) || 0,
      protein: Number(meal.protein) || 0,
      carbs: Number(meal.carbs) || 0,
      fats: Number(meal.fats) || 0,
      notes: meal.notes || "Logged from meal plan",
      recipe_data: Array.isArray(meal.recipes) ? meal.recipes : [],
      meal_plan_item_id: meal.id || null,
      source: "recipe",
    }));
  }, [logsForSelectedDate, programMeals]);

useEffect(() => {
  const next = {};

  safeMealFeedback.forEach((x) => {
    const payload = {
      rating: Number(x?.rating) || 0,
      note: x?.note || "",
    };

    if (x?.log_id) {
      next[String(x.log_id)] = payload;
    }
    if (x?.meal_plan_item_id) {
      next[String(x.meal_plan_item_id)] = payload;
    }
    if (x?.recipe_id) {
      next[`recipe:${String(x.recipe_id)}`] = payload;
    }
  });

  setMealRatings(next);
}, [safeMealFeedback]);

  const loadNutritionHabits = useCallback(async (dateStr) => {
    if (!token || !dateStr) {
      setNutritionHabits([]);
      return;
    }
    setNutritionHabitsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/today?date=${encodeURIComponent(dateStr)}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const all = Array.isArray(data?.habits) ? data.habits : [];
      setNutritionHabits(all.filter(isNutritionHabit));
    } catch (e) {
      console.warn('[FoodTracker] nutrition habits load:', e?.message || e);
      setNutritionHabits([]);
    } finally {
      setNutritionHabitsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadNutritionHabits(selectedDate);
  }, [loadNutritionHabits, selectedDate]);

  const toggleNutritionHabitComplete = async (habitId, checked) => {
    if (!token || !habitId) return;
    setNutritionHabitBusyId(String(habitId));
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/${habitId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ habit_id: habitId, date: selectedDate, checked: !!checked }),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadNutritionHabits(selectedDate);
      showToast?.('Habit updated.', 'success');
    } catch (e) {
      showToast?.(`Error updating habit: ${e?.message || String(e)}`, 'error');
    } finally {
      setNutritionHabitBusyId('');
    }
  };

  const createNutritionHabit = async (e) => {
    e?.preventDefault?.();
    if (!token) return;

    const name = String(newHabitForm.name || '').trim();
    if (!name) {
      showToast?.('Enter a habit name.', 'error');
      return;
    }

    const payload = {
      name,
      description: String(newHabitForm.description || '').trim(),
      habit_type: newHabitForm.habit_type,
      time_of_day: newHabitForm.time_of_day || '12:00',
      category: 'nutrition',
      start_date: selectedDate,
    };

    if (newHabitForm.habit_type === 'metric') {
      const value = Number(newHabitForm.metric_value);
      if (!Number.isFinite(value)) {
        showToast?.('Enter a valid metric target.', 'error');
        return;
      }
      payload.metric_rule = {
        domain: newHabitForm.metric_domain,
        field: newHabitForm.metric_field,
        operator: newHabitForm.metric_operator,
        value,
      };
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/habits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      setShowCreateHabitModal(false);
      setNewHabitForm({
        name: '',
        description: '',
        habit_type: 'metric',
        time_of_day: '12:00',
        metric_domain: 'app',
        metric_field: 'protein_g',
        metric_operator: 'gt',
        metric_value: '',
      });
      await loadNutritionHabits(selectedDate);
      showToast?.('Nutrition habit created.', 'success');
    } catch (e) {
      showToast?.(`Error creating habit: ${e?.message || String(e)}`, 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addDaysISO = (isoDate, deltaDays) => {
    const d = new Date(`${isoDate}T00:00:00`);
    d.setDate(d.getDate() + deltaDays);
    return toISODate(d);
  };

  const refreshFoodView = async () => {
    await Promise.resolve(onRefreshFood?.(selectedDate));
    await loadProgramMealsForDate(selectedDate);
    await loadNutritionHabits(selectedDate);
  };

  const saveMealRating = async ({
    key,
    meal_type,
    recipe_id = null,
    log_id = null,
    meal_plan_item_id = null,
    rating,
    note = ""
  }) => {
    if (!token) return;
    setMealRatingBusy(String(key));
    try {
      const payload = {
        date: selectedDate,
        meal_type,
        recipe_id,
        log_id,
        meal_plan_item_id,
        rating,
        note,
      };

      if (typeof onSaveMealFeedback === "function") {
        await Promise.resolve(onSaveMealFeedback(payload));
      } else {
        const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await res.text());
      }

      setMealRatings((prev) => ({
        ...prev,
        [key]: { rating, note },
      }));
      showToast?.("Meal rating saved.", "success");
    } catch (e) {
      showToast?.(`Error saving meal rating: ${e?.message || String(e)}`, "error");
    } finally {
      setMealRatingBusy("");
    }
  };

  const loadProgramMealsForDate = useCallback(async (dateStr) => {
    setProgramMealsLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/nutrition/meal-plan/day?date=${encodeURIComponent(dateStr)}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load meal plan for date");
      }

      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setProgramMeals(items);
    } catch (e) {
      console.warn("[FoodTracker] meal plan day load:", e.message);
      setProgramMeals([]);
    } finally {
      setProgramMealsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadProgramMealsForDate(selectedDate);
  }, [loadProgramMealsForDate, selectedDate]);

  const logProgramMeal = async (meal) => {
    if (!meal || typeof onManualLog !== 'function') return;
    const key = `${meal.id}:${meal.meal_type}`;
    setProgramMealBusyKey(key);
    try {
      await Promise.resolve(
        onManualLog({
          food_name: meal.meal_name || meal.name || meal.title || "Planned meal",
          quantity: '1 serving',
          meal_type: meal.meal_type,
          calories: Number(meal.calories) || 0,
          protein: Number(meal.protein) || 0,
          carbs: Number(meal.carbs) || 0,
          fats: Number(meal.fats) || 0,
          notes: meal.notes || 'Logged from meal plan',
          date: selectedDate,
          source: 'program_meal',
          recipe_data: meal.recipes || [],
          meal_plan_item_id: meal.id || null,
        })
      );
      showToast?.("Meal logged.", "success");
    } catch (e) {
      showToast?.(`Error logging meal plan item: ${e?.message || String(e)}`, "error");
    } finally {
      setProgramMealBusyKey('');
    }
  };

  const handleAutoFill = async () => {
    if (!formData.food_name.trim()) {
      showToast?.("Please enter a food name first", "error");
      return;
    }

    setIsAutoFilling(true);
    try {
      const form = new FormData();
      form.append('food_name', formData.food_name);
      form.append('quantity', formData.quantity || '1 serving');

      const response = await fetch(`${BACKEND_URL}/api/food/estimate-nutrition`, {
        method: 'POST',
        body: form
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          calories: data.calories?.toString() || '',
          protein: data.protein?.toString() || '',
          carbs: data.carbs?.toString() || '',
          fats: data.fats?.toString() || '',
          fiber: data.fiber?.toString() || ''
        }));
        showToast?.("Nutrition auto-filled.", "success");
      } else {
        showToast?.("Could not estimate nutrition. Please enter manually.", "error");
      }
    } catch (error) {
      showToast?.(`Error estimating nutrition: ${error.message}`, "error");
    }
    setIsAutoFilling(false);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    const foodData = {
      food_name: formData.food_name,
      quantity: formData.quantity,
      calories: parseFloat(formData.calories) || 0,
      protein: parseFloat(formData.protein) || 0,
      carbs: parseFloat(formData.carbs) || 0,
      fats: parseFloat(formData.fats) || 0,
      fiber: parseFloat(formData.fiber) || 0,
      meal_type: formData.meal_type,
      date: selectedDate,
    };

    await Promise.resolve(onManualLog?.(foodData));

    setFormData({
      food_name: '',
      quantity: '1 serving',
      meal_type: 'lunch',
      calories: '',
      protein: '',
      carbs: '',
      fats: '',
      fiber: ''
    });
    setShowManualForm(false);
    showToast?.("Food logged.", "success");
  };

  const handleQuickAddFood = async () => {
    const selected = quickAddFoodOptions.find((x) => x.key === quickFoodKey);
    if (!selected) {
      showToast?.("Select a food first", "error");
      return;
    }

    await Promise.resolve(
      onManualLog?.({
        food_name: selected.food_name,
        quantity: selected.quantity,
        meal_type: selected.meal_type,
        calories: selected.calories,
        protein: selected.protein,
        carbs: selected.carbs,
        fats: selected.fats,
        notes: selected.notes || "",
        recipe_data: selected.recipe_data || [],
        meal_plan_item_id: selected.meal_plan_item_id || null,
        date: selectedDate,
        source: selected.source === "history" ? "quick_add_history" : "quick_add_recipe",
      })
    );

    setQuickFoodKey("");
    setShowQuickAddForm(false);
    showToast?.("Food logged.", "success");
  };

  const handleHiddenUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPendingUploadFile(file);
    setShowUploadDetails(true);
  };

  const addWater = async (oz) => {
    const amount = Number(oz);
    if (!Number.isFinite(amount) || amount <= 0) return;

    if (typeof onAddWater !== 'function') {
      showToast?.('Water logging backend is not wired yet. Pass onAddWater + waterLogs props to FoodTracker.', 'error');
      return;
    }

    setWaterBusy(true);
    try {
      await onAddWater({ ounces: amount, date: selectedDate });
      setWaterOzCustom('');
      showToast?.("Water added.", "success");
    } catch (e) {
      showToast?.(`Error adding water: ${e?.message || String(e)}`, "error");
    } finally {
      setWaterBusy(false);
    }
  };

  const deleteWater = async (waterLogId) => {
    if (typeof onDeleteWaterLog !== 'function') {
      showToast?.('Water delete is not wired yet. Pass onDeleteWaterLog prop to FoodTracker.', 'error');
      return;
    }
    setWaterBusy(true);
    try {
      await onDeleteWaterLog(waterLogId);
      showToast?.("Water entry deleted.", "success");
    } catch (e) {
      showToast?.(`Error deleting water log: ${e?.message || String(e)}`, "error");
    } finally {
      setWaterBusy(false);
    }
  };

  const renderMealRatingStars = ({
    key,
    meal_type,
    recipe_id = null,
    log_id = null,
    meal_plan_item_id = null,
    rating = 0
  }) => {
    const current = Number(mealRatings?.[key]?.rating || rating);

    return (
      <div className="meal-rating-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className="icon-btn meal-rating-star"
            disabled={mealRatingBusy === String(key)}
            onClick={() =>
              saveMealRating({
                key,
                meal_type,
                recipe_id,
                log_id,
                meal_plan_item_id,
                rating: n,
                note: mealRatings?.[key]?.note || "",
              })
            }
            title={`Rate ${n} star${n === 1 ? "" : "s"}`}
            style={{
              fontSize: 18,
              color: n <= current ? "#f59e0b" : "var(--text-muted)",
            }}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  return (
  <div
    className="food-view"
    style={{
      maxWidth: 1200,
      margin: "0 auto",
      width: "100%",
    }}
  >
        <div className="card tracker-hero tracker-hero--food">
          <div className="tracker-hero-main tracker-hero-main--food">
            <div className="tracker-hero-copy tracker-hero-copy--food">
              <div className="tracker-hero-heading-row">
                <h1 className="heading-1" style={{ margin: 0 }}>
                  Food Tracker
                </h1>
                <button onClick={onOpenLogChoice} className="btn-primary tracker-hero-add-btn" type="button">
                  ➕ Add Food
                </button>
              </div>
              <div className="body-small food-mobile-hide-subheader" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                Log intake by photo, manual entry, favorites, and saved meals.
              </div>
            </div>

            <div className="food-date-toolbar">
              <div className="date-nav-inline food-date-nav-row">
                <button
                  type="button"
                  className="btn-secondary date-nav-btn food-date-arrow"
                  onClick={() => setSelectedDate((d) => addDaysISO(d, -1))}
                  aria-label="Previous day"
                  title="Previous day"
                >
                  ◀
                </button>

                <div className="form-group food-date-group" style={{ marginBottom: 0 }}>
                  <input
                    type="date"
                    className="form-input date-nav-date food-date-input"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn-secondary date-nav-btn food-date-arrow"
                  onClick={() => setSelectedDate((d) => addDaysISO(d, +1))}
                  aria-label="Next day"
                  title="Next day"
                >
                  ▶
                </button>
              </div>

              <div className="food-date-actions">
                {!isTodaySelected && (
                  <button
                    type="button"
                    className="btn-secondary food-date-action-btn"
                    onClick={() => setSelectedDate(today)}
                  >
                    Today
                  </button>
                )}

              </div>
            </div>
          </div>
        </div>

        {!onOpenLogChoice && (
          <div className="food-actions tracker-primary-actions tracker-primary-actions--food">
            <button onClick={onStartCamera} className="btn-primary" type="button">
              Take Photo
            </button>
            <button onClick={() => fileInputRefToUse.current?.click()} className="btn-secondary" type="button">
              Upload Photo
            </button>
          </div>
        )}

        <div className="form-section" style={{ marginTop: 16, marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 className="heading-3" style={{ marginBottom: 6 }}>Today's Nutrition Totals</h3>
              <div className="body-small food-mobile-hide-subheader" style={{ color: 'var(--text-secondary)' }}>
                Totals for {selectedDate === today ? 'today' : selectedDate}.
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <FoodMobileCarousel className="food-totals-grid" slideClassName="food-mobile-summary-slide">
              {[
                { label: 'Calories', value: dailyNutritionTotals.calories.toFixed(0), unit: 'cal' },
                { label: 'Protein', value: dailyNutritionTotals.protein.toFixed(1), unit: 'g' },
                { label: 'Carbs', value: dailyNutritionTotals.carbs.toFixed(1), unit: 'g' },
                { label: 'Fats', value: dailyNutritionTotals.fats.toFixed(1), unit: 'g' },
                { label: 'Fiber', value: dailyNutritionTotals.fiber.toFixed(1), unit: 'g' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="meal-card food-summary-card"
                  style={{
                    padding: 14,
                    border: '1px solid var(--border-light)',
                    background: 'var(--bg-secondary)',
                  }}
                >
                  <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: 6 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>
                    {item.value}
                  </div>
                  <div className="body-small" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                    {item.unit}
                  </div>
                </div>
              ))}
            </FoodMobileCarousel>
          </div>
        </div>

        <div className="form-section tracker-habits-section" style={{ marginTop: 16, marginBottom: 18 }}>
          <div className="tracker-section-header tracker-section-header--compact">
            <div>
              <h3 className="heading-3" style={{ marginBottom: 6 }}>Nutrition Habits</h3>
              <div className="body-small food-mobile-hide-subheader" style={{ color: 'var(--text-secondary)' }}>
                Daily nutrition habits and metric goals for {selectedDate === today ? 'today' : selectedDate}.
              </div>
            </div>
            <button
              type="button"
              className="tracker-add-icon-btn"
              onClick={() => setShowCreateHabitModal(true)}
              aria-label="New nutrition habit"
              title="New nutrition habit"
            >
              +
            </button>
          </div>

          {nutritionHabitsLoading ? (
            <div className="body-small" style={{ color: 'var(--text-muted)', marginTop: 12 }}>Loading habits…</div>
          ) : nutritionHabits.length === 0 ? (
            <div className="body-small" style={{ color: 'var(--text-muted)', marginTop: 12 }}>
              No nutrition habits yet for this day.
            </div>
          ) : (
            <div style={{ marginTop: 14 }}><FoodMobileCarousel className="tracker-habit-grid food-habits-carousel" slideClassName="food-mobile-habit-slide">
              {nutritionHabits.map((habit) => {
                const isMetric = habit.habit_type === 'metric' && habit.metric_rule;
                const isChecked = !!habit.completed;
                const rule = habit.metric_rule || null;
                const actual = habit.metric_actual ?? null;
                const helper = isMetric ? metricProgressText(rule, actual) : '';
                const visual = isMetric ? getMetricRingVisual(rule, actual) : null;

                return (
                  <div key={habit.habit_id || habit.id} className="tracker-habit-card meal-card food-habit-carousel-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="food-name">{habit.name || habit.title || 'Habit'}</div>
                        <div className="body-small" style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
                          {habit.time_of_day || '09:00'}{habit.description ? ` • ${habit.description}` : ''}
                        </div>
                      </div>
                      {!isMetric ? (
                        <button
                          type="button"
                          className={isChecked ? 'btn-primary' : 'btn-secondary'}
                          disabled={nutritionHabitBusyId === String(habit.habit_id || habit.id)}
                          onClick={() => toggleNutritionHabitComplete(habit.habit_id || habit.id, !isChecked)}
                        >
                          {nutritionHabitBusyId === String(habit.habit_id || habit.id) ? '…' : isChecked ? 'Undo' : 'Complete'}
                        </button>
                      ) : null}
                    </div>

                    {isMetric ? (
                      <div className="tracker-habit-metric-row" style={{ marginTop: 14 }}>
                        <MetricHabitRing
                          rule={rule}
                          actual={actual}
                          label={habit.name || habit.title || 'Metric habit'}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="food-nutrients">
                            Metric: {rule?.domain}.{rule?.field} {opLabel(rule?.operator)} {fmtNum(rule?.value)}
                          </div>
                          <div className="body-small" style={{ color: visual?.color || 'var(--text-secondary)', marginTop: 6, fontWeight: 700 }}>
                            {actual != null ? `Actual: ${fmtNum(actual)}` : 'Actual: —'}
                          </div>
                          <div className="body-small" style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
                            {helper}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="body-small" style={{ color: 'var(--text-secondary)', marginTop: 12 }}>
                        Checkbox habit • {isChecked ? 'Completed' : 'Not completed'}
                      </div>
                    )}
                  </div>
                );
              })}
            </FoodMobileCarousel></div>
          )}
        </div>

        {showQuickAddForm && (
          <div className="modal-overlay" style={{ zIndex: 2000000 }} onMouseDown={closeAllFoodModals}>
            <div
              className="modal-content ct-form-modal"
              onMouseDown={(e) => e.stopPropagation()}
              style={{ maxWidth: 620 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <h3 className="heading-3" style={{ margin: 0 }}>⚡ Quick Add Food</h3>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>✕</button>
              </div>

              <div className="form-group">
                <label className="body-medium">
                  {quickAddFoodOptions.some((x) => x.source === "history")
                    ? "Choose a previously logged food"
                    : "Choose from recipes / meal plan"}
                </label>

                <select
                  className="form-input"
                  value={quickFoodKey}
                  onChange={(e) => setQuickFoodKey(e.target.value)}
                >
                  <option value="">Select item</option>
                  {quickAddFoodOptions.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label} • {item.calories} cal • {item.source === "history" ? "previous log" : "recipe"}
                    </option>
                  ))}
                </select>
              </div>

              {quickFoodKey ? (() => {
                const selected = quickAddFoodOptions.find((x) => x.key === quickFoodKey);
                if (!selected) return null;

                return (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 12,
                      border: "1px solid var(--border-light)",
                      background: "var(--bg-secondary)",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{selected.food_name}</div>
                    <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
                      {selected.meal_type}
                      {" "}• {selected.calories} cal • P: {selected.protein}g • C: {selected.carbs}g • F: {selected.fats}g
                    </div>
                  </div>
                );
              })() : null}

              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={handleQuickAddFood} disabled={!quickFoodKey}>
                  Log Food
                </button>
              </div>
            </div>
          </div>
        )}

        {showManualForm && (
          <div className="modal-overlay" style={{ zIndex: 2000000 }} onMouseDown={closeAllFoodModals}>
            <div className="modal-content ct-form-modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h3 className="heading-3" style={{ margin: 0 }}>Manual Food Entry</h3>
                  <button type="button" className="btn-secondary" onClick={() => setShowAddWaterModal(true)}>Add Water</button>
                </div>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>✕</button>
              </div>

              <form onSubmit={handleManualSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="body-medium">Food Name</label>
                    <input
                      type="text"
                      name="food_name"
                      value={formData.food_name}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="e.g., Chicken Breast"
                      data-testid="manual-food-name"
                    />
                  </div>

                  <div className="form-group">
                    <label className="body-medium">Serving Size</label>
                    <input
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., 1 cup, 200g, 1 serving"
                      data-testid="manual-food-serving-size"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="body-medium">Meal Type</label>
                    <select
                      name="meal_type"
                      value={formData.meal_type}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      data-testid="manual-food-meal-type"
                    >
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch">Lunch</option>
                      <option value="dinner">Dinner</option>
                      <option value="snack">Snack</option>
                      <option value="beverage">Beverage</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={handleAutoFill}
                      disabled={isAutoFilling || !formData.food_name.trim()}
                      className="btn-secondary"
                      style={{ width: '100%' }}
                      data-testid="auto-fill-nutrition-btn"
                    >
                      {isAutoFilling ? 'Estimating...' : '✨ Auto-fill Nutrition'}
                    </button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="body-medium">Calories</label>
                    <input
                      type="number"
                      name="calories"
                      value={formData.calories}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="250"
                      data-testid="manual-food-calories"
                    />
                  </div>

                  <div className="form-group">
                    <label className="body-medium">Protein (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="protein"
                      value={formData.protein}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="20"
                      data-testid="manual-food-protein"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="body-medium">Carbs (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="carbs"
                      value={formData.carbs}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="30"
                      data-testid="manual-food-carbs"
                    />
                  </div>

                  <div className="form-group">
                    <label className="body-medium">Fats (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="fats"
                      value={formData.fats}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="10"
                      data-testid="manual-food-fats"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="body-medium">Fiber (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="fiber"
                      value={formData.fiber}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="5"
                      data-testid="manual-food-fiber"
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
                  <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" data-testid="log-food-btn">
                    Log Food
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {showAddWaterModal && (
          <div className="modal-overlay" style={{ zIndex: 2000001 }} onMouseDown={closeAllFoodModals}>
            <div className="modal-content ct-form-modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <h3 className="heading-3" style={{ margin: 0 }}>Add Water</h3>
                <button type="button" className="btn-secondary" onClick={() => setShowAddWaterModal(false)}>✕</button>
              </div>

              <div className="form-group">
                <label className="body-medium">Ounces</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="form-input"
                  value={waterOzCustom}
                  onChange={(e) => setWaterOzCustom(e.target.value)}
                  placeholder="e.g. 16"
                />
              </div>

              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
                <button type="button" className="btn-secondary" onClick={() => setShowAddWaterModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={waterBusy || !String(waterOzCustom).trim()}
                  onClick={async () => {
                    await addWater(waterOzCustom);
                    setShowAddWaterModal(false);
                  }}
                >
                  {waterBusy ? "Saving..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreateHabitModal && (
          <div className="modal-overlay" style={{ zIndex: 2000000 }} onMouseDown={closeAllFoodModals}>
            <div className="modal-content ct-form-modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <h3 className="heading-3" style={{ margin: 0 }}>New Nutrition Habit</h3>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>✕</button>
              </div>

              <form onSubmit={createNutritionHabit}>
                <div className="form-group">
                  <label className="body-medium">Habit Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newHabitForm.name}
                    onChange={(e) => setNewHabitForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Daily Protein Target"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">Description</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={newHabitForm.description}
                    onChange={(e) => setNewHabitForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional details"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="body-medium">Habit Type</label>
                    <select
                      className="form-input"
                      value={newHabitForm.habit_type}
                      onChange={(e) => setNewHabitForm((prev) => ({ ...prev, habit_type: e.target.value }))}
                    >
                      <option value="metric">Metric goal</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="body-medium">Time</label>
                    <input
                      type="time"
                      className="form-input"
                      value={newHabitForm.time_of_day}
                      onChange={(e) => setNewHabitForm((prev) => ({ ...prev, time_of_day: e.target.value }))}
                    />
                  </div>
                </div>

                {newHabitForm.habit_type === 'metric' && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="body-medium">Metric</label>
                        <select
                          className="form-input"
                          value={`${newHabitForm.metric_domain}.${newHabitForm.metric_field}`}
                          onChange={(e) => {
                            const [domain, field] = e.target.value.split('.');
                            setNewHabitForm((prev) => ({ ...prev, metric_domain: domain, metric_field: field }));
                          }}
                        >
                          <option value="app.calories_consumed">Calories</option>
                          <option value="app.protein_g">Protein (g)</option>
                          <option value="app.carbs_g">Carbs (g)</option>
                          <option value="app.fats_g">Fats (g)</option>
                          <option value="app.water_oz">Water (oz)</option>
                          <option value="food.calories">Food Calories</option>
                          <option value="food.protein">Food Protein</option>
                          <option value="food.carbs">Food Carbs</option>
                          <option value="food.fats">Food Fats</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="body-medium">Operator</label>
                        <select
                          className="form-input"
                          value={newHabitForm.metric_operator}
                          onChange={(e) => setNewHabitForm((prev) => ({ ...prev, metric_operator: e.target.value }))}
                        >
                          <option value="gt">&gt; Greater than</option>
                          <option value="lt">&lt; Less than</option>
                          <option value="eq">= Equal to</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="body-medium">Target Value</label>
                      <input
                        type="number"
                        className="form-input"
                        value={newHabitForm.metric_value}
                        onChange={(e) => setNewHabitForm((prev) => ({ ...prev, metric_value: e.target.value }))}
                        placeholder="e.g. 150"
                        required={newHabitForm.habit_type === 'metric'}
                      />
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>Cancel</button>
                  <button type="submit" className="btn-primary">Create Habit</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="logs-section">
          <h3 className="heading-3">
            Meals for {selectedDate === today ? "Today" : selectedDate}
          </h3>

          <FoodMobileCarousel className="food-meal-sections" slideClassName="food-mobile-meal-section-slide">
          {['breakfast', 'lunch', 'dinner', 'snack', 'beverage'].map(mealType => {
            const baseMealLogs = logsForSelectedDate.filter(log => String(log?.meal_type || '').toLowerCase() === mealType);
            const mealLogs = mealType === 'beverage' ? [...beverageWaterEntries, ...baseMealLogs] : baseMealLogs;
            const plannedMeals = visibleProgramMeals.filter(meal => String(meal?.meal_type || '').toLowerCase() === mealType);
            const mealCalories =
              mealLogs.reduce((sum, log) => sum + (Number(log.calories) || 0), 0) +
              plannedMeals.reduce((sum, meal) => sum + (Number(meal.calories) || 0), 0);

            return (
              <div key={mealType} className="meal-section">
                <div className="meal-header">
                  <h4 className="heading-4" style={{ textTransform: 'capitalize' }}>
                    {mealType === 'beverage' ? 'Beverages' : mealType}
                  </h4>
                  <span className="body-small">{mealCalories.toFixed(0)} cal</span>
                </div>

                {programMealsLoading ? (
                  <p className="body-small" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Loading meal program…
                  </p>
                ) : null}

                {plannedMeals.length > 0 ? (
                  <div className="meal-items" style={{ marginBottom: mealLogs.length ? 12 : 0 }}>
                    {plannedMeals.map(meal => {
                      const busyKey = `${meal.id}:${meal.meal_type}`;
                      return (
                        <div
                          key={meal.id}
                          className="meal-card"
                          style={{
                            background: 'rgba(255,255,255,0.03)',
                          }}
                        >
                          <div className="food-details">
                            <div className="food-name">
                              {meal.meal_name || meal.name || meal.title || "Planned meal"}
                            </div>
                            <div className="food-nutrients">
                              Meal program • {meal.calories} cal • P: {meal.protein}g • C: {meal.carbs}g • F: {meal.fats}g
                            </div>
                          </div>
                          <div className="meal-actions">
                            <button
                              type="button"
                              onClick={() => logProgramMeal(meal)}
                              className="btn-primary"
                              disabled={programMealBusyKey === busyKey}
                              title="Log this meal"
                              style={{ minWidth: 88 }}
                            >
                              {programMealBusyKey === busyKey ? '…' : 'Log'}
                            </button>

                            <button
                              type="button"
                              onClick={() => setRecipeMeal(meal)}
                              className="btn-secondary"
                              title="View recipe"
                            >
                              Recipe
                            </button>
                          </div>
                          <div className="meal-rating meal-rating-row" style={{ marginTop: 8 }}>
                              <div className="body-small meal-rating-label" style={{ color: "var(--text-secondary)", marginBottom: 4 }}>
                                Rate this meal
                              </div>
                              {renderMealRatingStars({
                                key: meal.id || (meal.recipes?.[0]?.recipe_id ? `recipe:${meal.recipes[0].recipe_id}` : `${meal.meal_type}-${meal.title}`),
                                meal_type: meal.meal_type,
                                recipe_id: meal.recipes?.[0]?.recipe_id || null,
                                meal_plan_item_id: meal.id || null,
                                rating: meal.rating
                              })}
                            </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {mealLogs.length === 0 && plannedMeals.length === 0 ? (
                  <p className="body-small" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No items logged
                  </p>
                ) : mealLogs.length > 0 ? (
                  <div className="meal-items">
                    {mealLogs.map(log => (
                      <div key={log.log_id} className="food-item">
                        {!log.isWater && log.image_base64 && (
                          <img
                            src={`data:image/jpeg;base64,${log.image_base64}`}
                            alt={log.food_name}
                            className="food-image"
                          />
                        )}
                        <div className="meal-card food-log-main">
                          <div className="food-log-toprow">
                            <div className="food-details">
                              <div className="food-name">{log.food_name}</div>
                              {log.quantity ? (
                                <div className="body-small" style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
                                  Serving size: {log.quantity}
                                </div>
                              ) : null}
                              <div className="food-nutrients">
                                {log.isWater
                                  ? `0 cal • Hydration entry${log.created_at ? ` • ${new Date(log.created_at).toLocaleTimeString()}` : ''}`
                                  : `${log.calories} cal • P: ${log.protein}g • C: ${log.carbs}g • F: ${log.fats}g • Fiber: ${Number(log.fiber ?? log.fiber_g ?? 0)}g`}
                              </div>
                            </div>
                            <div className="meal-actions food-log-actions">
                          {!log.isWater && Array.isArray(log.recipe_data) && log.recipe_data.length > 0 && (
                            <button
                              onClick={() => setRecipeMeal(log)}
                              className="icon-btn"
                              title="View recipe"
                            >
                              📖
                            </button>
                          )}
                          {!log.isWater && <button onClick={() => setEditingLog(log)} className="icon-btn" title="Edit">✏️</button>}
                          {!log.isWater && (
                            <button
                              onClick={() => onToggleFoodFavorite?.(log)}
                              className="icon-btn food-favorite-toggle"
                              title={getFavoriteForFood(log.food_name) ? "Remove from favorites" : "Add to favorites"}
                            >
                              {log.is_favorite ? "❤️" : "🤍"}
                            </button>
                          )}
                          <button onClick={() => log.isWater ? deleteWater(log.water_log_id) : onDeleteLog(log.log_id)} className="icon-btn" title="Delete">🗑️</button>
                            </div>
                          </div>
                         {!log.isWater ? (
                         <div className="meal-rating meal-rating-row">
                          <div style={{ marginTop: 8 }}>
                          <div className="body-small meal-rating-label" style={{ color: "var(--text-secondary)", marginBottom: 4 }}>
                            Rate this meal
                          </div>
                          {renderMealRatingStars({
                            key: log.log_id,
                            meal_type: log.meal_type,
                            recipe_id: Array.isArray(log.recipe_data) && log.recipe_data[0]?.recipe_id ? log.recipe_data[0].recipe_id : null,
                            log_id: log.log_id,
                            rating: log.rating
                          })}
                        </div>
                      </div>
                      ) : null}
                      </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
          </FoodMobileCarousel>
        </div>

        <input
          ref={fileInputRefToUse}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleHiddenUpload}
        />

        {editingLog && (
          <div
            className="modal-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onMouseDown={closeAllFoodModals}
          >
            <div
              className="modal-content ct-form-modal"
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                background: 'var(--bg-primary)',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '450px',
                width: '100%',
                overflowY: 'auto'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className="heading-3" style={{ margin: 0 }}>Edit Food Entry</h2>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>✕</button>
              </div>

              {editingLog.image_base64 && (
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <img
                    src={`data:image/jpeg;base64,${editingLog.image_base64}`}
                    alt={editingLog.food_name}
                    style={{ maxWidth: '100%', borderRadius: '8px', objectFit: 'cover' }}
                  />
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="body-medium">Food Name</label>
                <input
                  type="text"
                  value={editingLog.food_name}
                  onChange={(e) => setEditingLog(prev => ({ ...prev, food_name: e.target.value }))}
                  className="form-input"
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="body-medium">Meal Type</label>
                <select
                  value={editingLog.meal_type}
                  onChange={(e) => setEditingLog(prev => ({ ...prev, meal_type: e.target.value }))}
                  className="form-input"
                  style={{ fontSize: '16px' }}
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                  <option value="beverage">Beverage</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="body-medium">Serving Size</label>
                <input
                  type="text"
                  value={editingLog.quantity || ''}
                  onChange={(e) => setEditingLog(prev => ({ ...prev, quantity: e.target.value }))}
                  className="form-input"
                  placeholder="e.g. 1 bowl, 200g, 2 slices"
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div
                style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}
              >
                <div style={{ marginBottom: '8px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                  Nutrition
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="body-small" style={{ color: 'var(--text-muted)' }}>Calories</label>
                    <input
                      type="number"
                      value={editingLog.calories}
                      onChange={(e) => setEditingLog(prev => ({ ...prev, calories: parseInt(e.target.value) || 0 }))}
                      className="form-input"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="body-small" style={{ color: 'var(--text-muted)' }}>Protein (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingLog.protein}
                      onChange={(e) => setEditingLog(prev => ({ ...prev, protein: parseFloat(e.target.value) || 0 }))}
                      className="form-input"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="body-small" style={{ color: 'var(--text-muted)' }}>Carbs (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingLog.carbs}
                      onChange={(e) => setEditingLog(prev => ({ ...prev, carbs: parseFloat(e.target.value) || 0 }))}
                      className="form-input"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="body-small" style={{ color: 'var(--text-muted)' }}>Fats (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingLog.fats}
                      onChange={(e) => setEditingLog(prev => ({ ...prev, fats: parseFloat(e.target.value) || 0 }))}
                      className="form-input"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="body-small" style={{ color: 'var(--text-muted)' }}>Fiber (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingLog.fiber ?? editingLog.fiber_g ?? 0}
                      onChange={(e) => setEditingLog(prev => ({ ...prev, fiber: parseFloat(e.target.value) || 0 }))}
                      className="form-input"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={closeAllFoodModals} className="btn-secondary" style={{ flex: 1 }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onEditLog(editingLog);
                    setEditingLog(null);
                    showToast?.("Food entry updated.", "success");
                  }}
                  className="btn-primary"
                  style={{ flex: 1 }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {showUploadDetails && (
          <div
            className="modal-overlay"
            style={{ zIndex: 2000001 }}
            onMouseDown={closeAllFoodModals}
          >
            <div className="modal-content ct-form-modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <div className="heading-4" style={{ margin: 0 }}>Add details (optional)</div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeAllFoodModals}
                >
                  ✕
                </button>
              </div>

              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="body-medium">Details for the photo (optional)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder='e.g. "Chicken salad with dressing on the side"'
                />
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!pendingUploadFile}
                  onClick={() => {
                    const f = pendingUploadFile;
                    setShowUploadDetails(false);
                    setPendingUploadFile(null);
                    onFileUpload?.(f, details);
                    setDetails("");
                    showToast?.("Photo submitted for analysis.", "info");
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {recipeMeal && (
          <div className="modal-overlay" onMouseDown={closeAllFoodModals}>
            <div
              className="modal-content ct-form-modal"
              onMouseDown={(e) => e.stopPropagation()}
              style={{ maxWidth: 700 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 className="heading-3" style={{ margin: 0 }}>
                  {recipeMeal.meal_name || recipeMeal.name || recipeMeal.title || recipeMeal.food_name || "Recipe"}
                </h3>
                <button type="button" className="btn-secondary" onClick={closeAllFoodModals}>✕</button>
              </div>

              {(recipeMeal.recipe_data || recipeMeal.recipes || []).map((r, idx) => (
                <div key={idx} className="meal-card" style={{ padding: 14, marginTop: 10 }}>
                  <div style={{ fontWeight: 800 }}>{r.name || "Recipe"}</div>

                  {Array.isArray(r.ingredients) && r.ingredients.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>Ingredients</div>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {r.ingredients.map((ing, i) => (
                          <li key={i}>
                            {ing.quantity != null ? `${ing.quantity} ` : ""}
                            {ing.unit ? `${ing.unit} ` : ""}
                            {ing.name || ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!!(r.instructions || r.steps || r.directions) && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>Instructions</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {r.instructions || r.steps || r.directions}
                      </div>
                    </div>
                  )}

                  <div className="meal-rating meal-rating-row" style={{ marginTop: 8 }}>
                    <div className="body-small meal-rating-label" style={{ color: "var(--text-secondary)", marginBottom: 4 }}>
                      Rate this meal
                    </div>
                    {renderMealRatingStars({
                      key: recipeMeal.id || (recipeMeal.recipes?.[0]?.recipe_id ? `recipe:${recipeMeal.recipes[0].recipe_id}` : `${recipeMeal.meal_type}-${recipeMeal.title}`),
                      meal_type: recipeMeal.meal_type,
                      recipe_id: recipeMeal.recipes?.[0]?.recipe_id || null,
                      meal_plan_item_id: recipeMeal.id || null,
                      rating: recipeMeal.rating
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
}

export default FoodTracker;